import { useMutation } from '@apollo/client';
import { Col, notification, Row } from 'antd';
import Avatar from 'antd/lib/avatar/avatar';
import { ColumnsType } from 'antd/lib/table';
import ButtonWithSpinner from 'components/common/ButtonWithSpinner';
import SearchBarSuggestion from 'components/common/SearchBarSuggestion';
import Table from 'components/Table';
import { MobileTableType } from 'components/Table/types';
import useReactiveQueryTable from 'hooks/useReactiveQueryTable';
import React, { useCallback, useMemo, useState } from 'react';
import { SearchScopeEnum } from 'redux/search/types';
import {
  CREATE_APPOINTMENT_ASSIGNMENT,
  DELETE_APPOINTMENT_ASSIGNMENT,
} from 'services/graphql/mutations/appointmentAssignments';
import { GET_NOTARY_FOR_ASSIGNMENT } from 'services/graphql/queries/user.query';
import { AssignmentStatus } from 'types/appointmentAssignments';
import { IAppointmentState } from 'types/appointments';
import { GeneralFunction, ICommonCallbackPayload } from 'types/common';
import { ContactNumberType, INotary } from 'types/user';
import { convertDateToISO, formatAddress, getDefaultAvatarUrl, metersToMiles } from 'utils';

import { TextOnlyStyled } from '../../../styled';
import { ActionButtonContainerStyled, SearchBarContainerStyled } from './styled';

const NotaryTable: React.FC<{
  selectedAppointment?: IAppointmentState;
  refetchTransactionData?: GeneralFunction;
  callbacks?: ICommonCallbackPayload;
}> = ({ selectedAppointment, refetchTransactionData, callbacks }) => {
  const [requestLoadingState, setRequestLoadingState] = useState<Record<string, boolean>>({});
  const [searchInput, setSearchInput] = useState('');

  const onSearch = useCallback((v: string) => {
    setSearchInput(v);
  }, []);

  const mutationComplete = (error?: any) => {
    if (!error) {
      notification.success({
        message: 'Request changed successfully!',
        duration: 2,
      });
      refetchTransactionData?.();
      callbacks?.callbackOnSuccess?.();
    } else {
      console.error('Error', error);
      notification.error({
        message: 'Request error! Please try again',
        duration: 2,
      });
    }
  };

  const [createAssignment] = useMutation(CREATE_APPOINTMENT_ASSIGNMENT);
  const [deleteAssignment] = useMutation(DELETE_APPOINTMENT_ASSIGNMENT);

  const changeRequestLoadingStatus = (notaryId: string, value: boolean) => {
    setRequestLoadingState((c) => ({ ...c, [notaryId]: value }));
  };

  const onRequestNotary = async (notaryId: string) => {
    changeRequestLoadingStatus(notaryId, true);
    try {
      await createAssignment({
        variables: {
          doc: {
            status: AssignmentStatus.PENDING,
            notary: notaryId,
            appointment: selectedAppointment?.id || '',
          },
        },
      });
      mutationComplete();
    } catch (err) {
      mutationComplete(err);
    } finally {
      changeRequestLoadingStatus(notaryId, false);
    }
  };

  const onCancelNotary = async (assignmentId: string, notaryId: string) => {
    changeRequestLoadingStatus(notaryId, true);
    try {
      await deleteAssignment({
        variables: {
          id: assignmentId,
        },
      });
      mutationComplete();
    } catch (err) {
      mutationComplete(err);
    } finally {
      changeRequestLoadingStatus(notaryId, false);
    }
  };

  const notaryColumn: ColumnsType<any> = useMemo(
    () => [
      {
        title: 'Full Name',
        dataIndex: 'nameDetail',
        render: (nameDetail: { name: string; avatar: string }) => (
          <div className='gx-d-flex gx-align-items-center'>
            <Avatar src={nameDetail.avatar} className='gx-mr-2' />
            <div style={{ flex: '1' }} className='gx-font-weight-medium'>
              {nameDetail.name}
            </div>
          </div>
        ),
        // sorter: (a: ITransactionListTableProps, b: ITransactionListTableProps) => a.reference.localeCompare(b.reference),
      },
      {
        title: 'Email',
        dataIndex: 'email',
        render: (email: string) => (
          <div>
            <span
              className=''
              style={{
                wordBreak: 'break-all',
              }}>
              {email}
            </span>
          </div>
        ),
        // sorter: (a: ITransactionListTableProps, b: ITransactionListTableProps) => a.reference.localeCompare(b.reference),
      },
      {
        title: 'Phone',
        dataIndex: 'phone',
        render: (phone: string) => <span style={{ wordBreak: 'break-word' }}>{phone}</span>,
        // sorter: (a: ITransactionListTableProps, b: ITransactionListTableProps) => a.reference.localeCompare(b.reference),
      },
      // {
      //   title: 'State',
      //   dataIndex: 'state',
      //   // render: (distance: string) => (
      //   //   <span className=''>{metersToMiles(parseFloat(distance || '0')).toFixed(1)} miles</span>
      //   // ),
      //   // sorter: (a: ITransactionListTableProps, b: ITransactionListTableProps) => a.reference.localeCompare(b.reference),
      // },
      {
        title: 'Distance',
        dataIndex: 'distance',
        render: (distance: string) => (
          <span className=''>{metersToMiles(parseFloat(distance || '0')).toFixed(1)} miles</span>
        ),
        // sorter: (a: ITransactionListTableProps, b: ITransactionListTableProps) => a.reference.localeCompare(b.reference),
      },
      {
        title: 'Availability',
        dataIndex: 'availability',
        render: (availability: string) => <span className=''>{availability ? 'Yes' : 'No'}</span>,
        // sorter: (a: ITransactionListTableProps, b: ITransactionListTableProps) => a.reference.localeCompare(b.reference),
      },
      {
        title: '',
        dataIndex: 'action',
        render: ({
          notaryId,
          status,
          assignmentId,
        }: {
          notaryId: string;
          status: AssignmentStatus;
          assignmentId: string;
        }) => {
          const isRequestProcessing = requestLoadingState[notaryId];
          const actionButtons = () => {
            switch (status) {
              case AssignmentStatus.PENDING:
                return (
                  <Row className='gx-m-0' align='middle'>
                    <Col className='gx-p-0' span={12}>
                      <TextOnlyStyled className='gx-m-0 gx-text-center' $color='#F7CB73'>
                        Requested
                      </TextOnlyStyled>
                    </Col>
                    <Col className='gx-pr-0 gx-pl-1' span={12}>
                      <ButtonWithSpinner
                        buttonType='text'
                        width='100%'
                        color='red'
                        isLoading={isRequestProcessing}
                        spinnerColor='#038fde'
                        onClick={async () => {
                          onCancelNotary(assignmentId, notaryId);
                          // onCancelRequestNotaryClick(notaryId);
                        }}>
                        Cancel
                      </ButtonWithSpinner>
                    </Col>
                  </Row>
                );

              case AssignmentStatus.APPROVED:
                return (
                  <Row className='gx-m-0' align='middle'>
                    <Col className='gx-p-0  gx-text-center' span={12}>
                      <TextOnlyStyled className='gx-m-0' $color='#81D742'>
                        Accepted
                      </TextOnlyStyled>
                    </Col>
                    <Col className='gx-pr-0 gx-pl-1' span={12}>
                      <ButtonWithSpinner
                        buttonType='text'
                        width='100%'
                        color='#038fde'
                        spinnerColor='#038fde'
                        isLoading={isRequestProcessing}
                        onClick={() => {
                          onCancelNotary(assignmentId, notaryId);
                        }}>
                        Cancel
                      </ButtonWithSpinner>
                    </Col>
                  </Row>
                );

              case AssignmentStatus.REJECTED:
                return (
                  <Row className='gx-m-0' align='middle'>
                    <Col className='gx-p-0' span={12}>
                      <TextOnlyStyled className='gx-m-0' $color='red'>
                        Rejected
                      </TextOnlyStyled>
                    </Col>
                    <Col className='gx-pr-0 gx-pl-1' span={12}>
                      <ButtonWithSpinner
                        buttonType='text'
                        width='100%'
                        color='#038fde'
                        spinnerColor='#038fde'
                        isLoading={isRequestProcessing}
                        onClick={() => {
                          onRequestNotary(notaryId);
                          // onRequestNotaryClick(notaryId);
                        }}>
                        Request
                      </ButtonWithSpinner>
                    </Col>
                  </Row>
                );

              default:
                return (
                  <Row className='gx-m-0' align='middle'>
                    <Col className='gx-p-0 gx-flex-row gx-justify-content-center' span={24}>
                      <ButtonWithSpinner
                        className='gx-p-0'
                        buttonType='text'
                        width='100%'
                        color='#038fde'
                        spinnerColor='#038fde'
                        isLoading={isRequestProcessing}
                        onClick={async () => {
                          onRequestNotary(notaryId);
                          // onRequestNotaryClick(notaryId);
                        }}>
                        Request
                      </ButtonWithSpinner>
                    </Col>
                  </Row>
                );
            }
          };
          return <ActionButtonContainerStyled>{actionButtons()}</ActionButtonContainerStyled>;
        },
      },
    ],
    [selectedAppointment, requestLoadingState],
  );

  const dataMapper = useCallback(
    (notary: INotary) => {
      // if (appointmentDay && appointmentTime && availability) {
      //   availability.some((av) => {
      //     if (av.day.toLowerCase() !== DAYS[appointmentDay].toLowerCase()) return false;
      //     const suitableAvailableTimeObject = av.worktimes.find((wt) => {
      //       const wtFromInMinute = convertTimeToMinute(wt.from);
      //       const wtToInMinute = convertTimeToMinute(wt.to);
      //       if (wtFromInMinute <= appointmentTimeInMinutes && wtToInMinute >= totalAppointmentTime) {
      //         return true;
      //       }
      //       return false;
      //     });
      //     if (suitableAvailableTimeObject) {
      //       availableTime = `${suitableAvailableTimeObject.from} - ${suitableAvailableTimeObject.to}`;
      //       return true;
      //     }
      //     return false;
      //   });
      // }
      const suggestedNotaryId = notary.id;
      const assignments = selectedAppointment?.assignments;
      const { status: notaryRequestStatus, id: assignmentId } =
        assignments?.find((assignment) => assignment.notary.id === suggestedNotaryId) || {};
      const isAvailable = notaryRequestStatus === AssignmentStatus.APPROVED ? 'Yes' : notary.isAvailable;
      return {
        key: notary.id,
        nameDetail: {
          name: notary.firstName ? `${notary.firstName} ${notary.lastName}` : 'None',
          avatar:
            notary.avatar || getDefaultAvatarUrl(notary.firstName ? `${notary.firstName} ${notary.lastName}` : ''),
        },
        state: notary.state,
        distance: notary.transactionDistance,
        email: notary.email,
        phone:
          notary.contacts?.find((numberDetail) => numberDetail.numberType === ContactNumberType.CELL)?.number || 'None',
        availability: isAvailable,
        action: {
          status: notaryRequestStatus,
          notaryId: suggestedNotaryId,
          assignmentId,
        },
      };
    },
    [selectedAppointment],
  );

  const tableProps = useReactiveQueryTable({
    graphQLQuery: GET_NOTARY_FOR_ASSIGNMENT,
    columns: notaryColumn,
    dataMapper,
    queryProps: {
      variables: {
        pagination: {
          take: 10,
        },
        transactionAddress: formatAddress(selectedAppointment?.address),
        matchState: selectedAppointment?.address?.state,
        dateTime: convertDateToISO(selectedAppointment?.date || ''),
        duration: selectedAppointment?.duration,
        query: searchInput || undefined,
        requiredScanDoc: !!selectedAppointment?.transaction?.requiredScanDoc,
        appointmentCoordinate: selectedAppointment?.address?.coordinate,
      },
      skip: !selectedAppointment,
    },
  });

  return (
    <Row className='gx-m-0 gx-p-0'>
      <SearchBarContainerStyled>
        <SearchBarSuggestion onSearch={onSearch} entities={[SearchScopeEnum.USER]} />
      </SearchBarContainerStyled>
      <Col span={24}>
        <Table
          className='gx-table-responsive gx-table-no-bordered'
          {...tableProps}
          mobileDesignType={MobileTableType.RESPONSIVE_TABLE}
          widthThreshold={575}
        />
      </Col>
    </Row>
  );
};

export default React.memo(NotaryTable);
